<template>
  <div class="admin-container">
    <!-- Sidebar -->
    <AdminSidebar :isOpen="isSidebarOpen" :userData="currentUser" />

    <!-- Main Content -->
    <div class="main-content" :class="{ 'content-shifted': isSidebarOpen }">
      <nav class="admin-nav">
        <button class="menu-btn" @click="toggleSidebar">
          <i class="fas fa-bars"></i>
        </button>
      </nav>

      <div class="admin-content">
        <div class="section-header">
          <h2>Quản Lý Tuyến Đường</h2>
          <div class="header-actions">
            <div class="filter-select-group">
              <select
                v-model="filterMonth"
                @change="applyFilters"
                class="filter-select"
              >
                <option value="">Tháng</option>
                <option v-for="month in months" :key="month" :value="month">
                  {{ month }}
                </option>
              </select>
              <select
                v-model="filterYear"
                @change="applyFilters"
                class="filter-select"
              >
                <option value="">Năm</option>
                <option v-for="year in years" :key="year" :value="year">
                  {{ year }}
                </option>
              </select>
              <button class="filter-reset" @click="clearFilters">
                <i class="fas fa-undo"></i>
              </button>
            </div>
            <div class="search-box">
              <i class="fas fa-search"></i>
              <input
                type="text"
                v-model="searchQuery"
                placeholder="Tìm theo điểm đến..."
                @keyup.enter="handleSearch"
              />
              <button class="search-btn" @click="handleSearch">
                <i class="fas fa-search"></i>
              </button>
            </div>
            <button class="add-btn" @click="openAddModal">
              <i class="fas fa-plus"></i> Thêm mới
            </button>
          </div>
        </div>

        <div class="table-container">
          <!-- Loading State -->
          <div v-if="isLoading" class="table-loading">
            <div class="table-spinner"></div>
            <p>Đang tải dữ liệu...</p>
          </div>

          <!-- Empty State -->
          <div v-else-if="routes.length === 0" class="table-empty">
            <i class="fas fa-route table-empty-icon"></i>
            <h3 class="table-empty-title">Không có dữ liệu tuyến đường</h3>
            <p class="table-empty-text">Chưa có tuyến đường nào được thêm vào hệ thống</p>
            <button class="table-empty-action" @click="openAddModal">
              <i class="fas fa-plus"></i> Thêm tuyến đường
            </button>
          </div>

          <!-- Table Content -->
          <div v-else class="table-responsive has-mobile-cards">
            <!-- Desktop Table -->
            <table class="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Điểm đến</th>
                  <th>Tháng</th>
                  <th>Năm</th>
                  <th class="text-right">Giá (VND)</th>
                  <th class="col-actions">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="route in filteredRoutes" :key="route.route_id">
                  <td class="nowrap">#{{ route.route_id }}</td>
                  <td>
                    <span class="text-truncate" :title="route.destination_location">
                      {{ route.destination_location }}
                    </span>
                  </td>
                  <td>{{ route.month || '-' }}</td>
                  <td>{{ route.year || '-' }}</td>
                  <td class="text-right nowrap price-value">{{ formatCurrency(route.price) }}</td>
                  <td class="col-actions">
                    <div class="action-buttons">
                      <button
                        @click="viewRouteDetails(route)"
                        class="action-btn view-btn"
                        title="Xem chi tiết"
                        aria-label="Xem chi tiết tuyến đường"
                      >
                        <i class="fas fa-eye"></i>
                      </button>
                      <button 
                        @click="openEditModal(route)" 
                        class="action-btn edit-btn"
                        title="Chỉnh sửa"
                        aria-label="Chỉnh sửa tuyến đường"
                      >
                        <i class="fas fa-edit"></i>
                      </button>
                      <button
                        @click="confirmDelete(route)"
                        class="action-btn delete-btn"
                        title="Xóa"
                        aria-label="Xóa tuyến đường"
                      >
                        <i class="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Mobile Card View -->
            <div class="mobile-card-list">
              <div v-for="route in filteredRoutes" :key="'card-' + route.route_id" class="mobile-card">
                <div class="mobile-card-header">
                  <div class="mobile-card-title">
                    <span class="mobile-card-id">#{{ route.route_id }}</span>
                    {{ route.destination_location }}
                  </div>
                </div>
                <div class="mobile-card-body">
                  <div class="mobile-card-row" v-if="route.month || route.year">
                    <span class="mobile-card-label">Thời gian:</span>
                    <span class="mobile-card-value">
                      {{ route.month ? `Tháng ${route.month}` : '' }}
                      {{ route.month && route.year ? '/' : '' }}
                      {{ route.year || '' }}
                    </span>
                  </div>
                  <div class="mobile-card-row">
                    <span class="mobile-card-label">Giá:</span>
                    <span class="mobile-card-value price-value">{{ formatCurrency(route.price) }}</span>
                  </div>
                </div>
                <div class="mobile-card-actions">
                  <button class="action-btn view-btn with-label" @click="viewRouteDetails(route)">
                    <i class="fas fa-eye"></i> Chi tiết
                  </button>
                  <button class="action-btn edit-btn with-label" @click="openEditModal(route)">
                    <i class="fas fa-edit"></i> Sửa
                  </button>
                  <button class="action-btn delete-btn with-label" @click="confirmDelete(route)">
                    <i class="fas fa-trash-alt"></i> Xóa
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div class="pagination-container" v-if="!isLoading && routes.length > 0">
          <div class="pagination-info">
            <span v-if="hasFilters">
              Hiển thị kết quả với:
              <template v-if="filterMonth">Tháng: {{ filterMonth }}</template>
              <template v-if="filterMonth && filterYear">, </template>
              <template v-if="filterYear">Năm: {{ filterYear }}</template>
              <template v-if="(filterMonth || filterYear) && searchTerm">, </template>
              <template v-if="searchTerm">Tìm kiếm: "{{ searchTerm }}"</template>
            </span>
          </div>
          <div class="pagination-controls">
            <button
              class="pagination-btn"
              :disabled="currentPage === 1"
              @click="changePage(currentPage - 1)"
            >
              <i class="fas fa-chevron-left"></i> Trước
            </button>
            <span class="page-info">
              Trang {{ currentPage }} / {{ totalPages }}
            </span>
            <button
              class="pagination-btn"
              :disabled="currentPage === totalPages || routes.length === 0"
              @click="changePage(currentPage + 1)"
            >
              Sau <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Route Modal -->
    <div class="modal" v-if="showModal" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>
            {{ isEditing ? 'Chỉnh sửa tuyến đường' : 'Thêm tuyến đường mới' }}
          </h2>
          <button class="close-btn" @click="closeModal">×</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitRouteForm">
            <div class="form-group">
              <label>Điểm đến:</label>
              <input
                type="text"
                v-model="routeForm.destination_location"
                required
                placeholder="Nhập tên điểm đến"
              />
            </div>
            <div class="form-group">
              <label>Tháng:</label>
              <select v-model="routeForm.month">
                <option value="">-- Chọn tháng --</option>
                <option v-for="month in months" :key="month" :value="month">
                  {{ month }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label>Năm:</label>
              <input
                type="text"
                v-model="routeForm.year"
                placeholder="Nhập năm"
              />
            </div>
            <div class="form-group">
              <label>URL hình ảnh:</label>
              <input
                type="text"
                v-model="routeForm.image_url"
                placeholder="Nhập URL hình ảnh"
              />
            </div>
            <div class="form-group">
              <label>Giá (VND):</label>
              <input
                type="number"
                v-model="routeForm.price"
                required
                placeholder="Nhập giá tiền"
              />
            </div>

            <div class="modal-footer">
              <button type="button" class="cancel-btn" @click="closeModal">
                Hủy
              </button>
              <button type="submit" class="save-btn">
                {{ isEditing ? 'Cập nhật' : 'Thêm mới' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Confirm Delete Modal -->
    <div
      class="modal"
      v-if="showDeleteConfirm"
      @click.self="showDeleteConfirm = false"
    >
      <div class="modal-content confirm-modal">
        <div class="modal-header">
          <h2>Xác nhận xóa</h2>
          <button class="close-btn" @click="showDeleteConfirm = false">
            ×
          </button>
        </div>
        <div class="modal-body">
          <p>
            Bạn có chắc chắn muốn xóa tuyến đường
            <strong>"{{ routeToDelete?.destination_location }}"</strong>?
          </p>
          <p class="warning">
            Lưu ý: Hành động này không thể hoàn tác và sẽ xóa tất cả chuyến đi
            liên quan.
          </p>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="cancel-btn"
            @click="showDeleteConfirm = false"
          >
            Hủy
          </button>
          <button type="button" class="delete-btn" @click="deleteRoute">
            Xóa
          </button>
        </div>
      </div>
    </div>

    <!-- Route Details Modal -->
    <div class="modal" v-if="showDetailsModal" @click.self="closeDetailsModal">
      <div class="modal-content details-modal">
        <div class="modal-header">
          <h2>Chi tiết tuyến đường</h2>
          <button class="close-btn" @click="closeDetailsModal">×</button>
        </div>
        <div class="modal-body">
          <!-- Route info section -->
          <div class="details-section">
            <h3><i class="fas fa-route"></i> Thông tin tuyến đường</h3>
            <div class="route-info-grid">
              <div class="info-item">
                <span class="label">ID:</span>
                <span class="value">#{{ selectedRoute?.route_id }}</span>
              </div>
              <div class="info-item">
                <span class="label">Điểm đến:</span>
                <span class="value">{{
                  selectedRoute?.destination_location
                }}</span>
              </div>
              <div class="info-item">
                <span class="label">Tháng/Năm:</span>
                <span class="value">
                  {{
                    selectedRoute?.month ? `Tháng ${selectedRoute.month}` : ''
                  }}
                  {{ selectedRoute?.month && selectedRoute?.year ? '/' : '' }}
                  {{ selectedRoute?.year ? `Năm ${selectedRoute.year}` : '' }}
                  {{
                    !selectedRoute?.month && !selectedRoute?.year
                      ? 'Không có'
                      : ''
                  }}
                </span>
              </div>
              <div class="info-item">
                <span class="label">Giá:</span>
                <span class="value">{{
                  formatCurrency(selectedRoute?.price)
                }}</span>
              </div>
            </div>
          </div>

          <!-- Related trips -->
          <div class="details-section">
            <h3><i class="fas fa-bus"></i> Chuyến đi liên quan</h3>

            <div v-if="isLoadingTrips" class="loading-indicator">
              <i class="fas fa-spinner fa-spin"></i> Đang tải dữ liệu...
            </div>

            <div v-else-if="relatedTrips.length === 0" class="empty-state">
              <i class="fas fa-info-circle"></i>
              <p>Chưa có chuyến đi nào cho tuyến đường này</p>
            </div>

            <div v-else class="trips-accordion">
              <div
                v-for="trip in relatedTrips"
                :key="trip.trip_id"
                class="trip-item"
              >
                <div
                  class="trip-header"
                  @click="toggleTripDetails(trip.trip_id)"
                >
                  <div class="trip-summary">
                    <div class="trip-time">
                      <i class="far fa-clock"></i>
                      {{ formatDateTime(trip.departure_datetime) }}
                    </div>
                    <div class="trip-status" :class="trip.status">
                      {{ translateTripStatus(trip.status) }}
                    </div>
                  </div>
                  <i
                    :class="
                      expandedTrips.includes(trip.trip_id)
                        ? 'fas fa-chevron-up'
                        : 'fas fa-chevron-down'
                    "
                  ></i>
                </div>
                <div
                  class="trip-details"
                  v-if="expandedTrips.includes(trip.trip_id)"
                >
                  <div class="trip-detail-grid">
                    <div class="trip-detail-item">
                      <span class="label">ID:</span>
                      <span class="value">#{{ trip.trip_id }}</span>
                    </div>
                    <div class="trip-detail-item">
                      <span class="label">Tên chuyến:</span>
                      <span class="value">{{ trip.name }}</span>
                    </div>
                    <div class="trip-detail-item" v-if="trip.name">
                      <span class="label">Tên chuyến:</span>
                      <span class="value">{{ trip.name }}</span>
                    </div>
                    <div class="trip-detail-item">
                      <span class="label">Thời gian khởi hành:</span>
                      <span class="value">{{
                        formatDateTime(trip.departure_datetime)
                      }}</span>
                    </div>
                    <div class="trip-detail-item">
                      <span class="label">Trạng thái:</span>
                      <span class="value status-badge" :class="trip.status">
                        {{ translateTripStatus(trip.status) }}
                      </span>
                    </div>
                    <div class="trip-detail-item">
                      <span class="label">Phương tiện:</span>
                      <span class="value">{{
                        trip.vehicle
                          ? formatVehicleType(trip.vehicle.type)
                          : 'Chưa phân bổ'
                      }}</span>
                    </div>
                    <div class="trip-detail-item">
                      <span class="label">Tài xế:</span>
                      <span class="value">
                        {{ getDriverName(trip) }}
                      </span>
                    </div>
                    <div class="trip-detail-item">
                      <span class="label">Biển số xe:</span>
                      <span class="value">
                        {{ getLicensePlate(trip) }}
                      </span>
                    </div>
                    <div class="trip-detail-item">
                      <span class="label">Số đặt vé:</span>
                      <span class="value">{{
                        trip.bookings ? trip.bookings.length : 0
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="close-details-btn"
            @click="closeDetailsModal"
          >
            Đóng
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axiosInstance from '@/plugins/axios'
import AdminSidebar from '../common/AdminSidebar.vue'
import { ToastService } from '@/services/ToastService'

const isProd = process.env.VUE_APP_IS_PROD === 'true'
const API_BASE_URL = isProd
  ? process.env.VUE_APP_BACKEND_URL_PROD
  : process.env.VUE_APP_BACKEND_URL_DEV

export default {
  name: 'AdminRoutes',
  components: {
    AdminSidebar
  },
  data () {
    return {
      showDetailsModal: false,
      selectedRoute: null,
      relatedTrips: [],
      expandedTrips: [],
      isLoadingTrips: false,
      routes: [],
      isSidebarOpen: false,
      searchQuery: '',
      searchTerm: '', // Giá trị đã tìm kiếm
      showModal: false,
      showDeleteConfirm: false,
      isEditing: false,
      routeToDelete: null,
      routeForm: {
        destination_location: '',
        month: '',
        year: '',
        image_url: '',
        price: 0
      },
      currentUser: {
        full_name: 'Admin User',
        role: 'Administrator'
      },
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
      months: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'],
      years: ['2025', '2026'],
      filterMonth: '',
      filterYear: '',
      isLoading: false
    }
  },
  computed: {
    userInitials () {
      return this.currentUser.full_name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
    },
    filteredRoutes () {
      return this.routes
    },
    totalPages () {
      return this.totalItems
        ? Math.ceil(this.totalItems / this.itemsPerPage)
        : 1
    },
    hasFilters () {
      return this.filterMonth || this.filterYear || this.searchTerm
    }
  },
  methods: {
    async fetchRoutes () {
      this.isLoading = true
      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        // Tính toán skip dựa trên currentPage và itemsPerPage
        const skip = (this.currentPage - 1) * this.itemsPerPage

        // Tạo đối tượng params với các giá trị mặc định
        const params = {
          skip: skip,
          limit: this.itemsPerPage
        }

        // Thêm các tham số filter nếu có
        if (this.filterMonth) {
          params.month = this.filterMonth
        }
        if (this.filterYear) {
          params.year = this.filterYear
        }
        if (this.searchTerm) {
          params.search = this.searchTerm
        }

        // Send request to the backend route endpoint
        const response = await axiosInstance.get(
          `${API_BASE_URL}/admin/routes/`,
          {
            headers,
            params
          }
        )

        // Cập nhật dữ liệu và tổng số item
        this.routes = response.data

        // Kiểm tra nếu đang không có trang dữ liệu thì quay về trang 1
        if (this.routes.length === 0 && this.currentPage > 1) {
          this.currentPage = 1
          this.fetchRoutes()
          return
        }

        // Nếu backend không cung cấp total_count, sử dụng độ dài mảng routes
        this.totalItems =
          response.data.length >= this.itemsPerPage
            ? this.currentPage * this.itemsPerPage + 1 // Ước tính còn trang tiếp theo
            : (this.currentPage - 1) * this.itemsPerPage + response.data.length // Đây là trang cuối cùng
      } catch (error) {
        console.error('Lỗi khi lấy dữ liệu tuyến đường:', error)
        if (error.response?.status === 401) {
          this.handleLogout()
        }
      } finally {
        this.isLoading = false
      }
    },
    handleSearch () {
      this.searchTerm = this.searchQuery.trim()
      this.currentPage = 1 // Reset về trang đầu tiên
      this.fetchRoutes()
    },
    applyFilters () {
      this.currentPage = 1 // Reset về trang đầu tiên khi lọc
      this.fetchRoutes()
    },
    clearFilters () {
      this.filterMonth = ''
      this.filterYear = ''
      this.searchQuery = ''
      this.searchTerm = ''
      this.currentPage = 1
      this.fetchRoutes()
    },
    handleLogout () {
      localStorage.removeItem('adminToken')
      localStorage.removeItem('refreshToken')
      this.$router.push('/login')
    },
    formatCurrency (amount) {
      return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND'
      }).format(amount)
    },
    changePage (newPage) {
      this.currentPage = newPage
      this.fetchRoutes()
    },
    openAddModal () {
      this.isEditing = false
      this.routeForm = {
        destination_location: '',
        month: '',
        year: '',
        image_url: '',
        price: 0
      }
      this.showModal = true
    },
    openEditModal (route) {
      this.isEditing = true
      this.routeForm = { ...route }
      this.showModal = true
    },
    closeModal () {
      this.showModal = false
    },
    confirmDelete (route) {
      this.routeToDelete = route
      this.showDeleteConfirm = true
    },
    getDriverName (trip) {
      if (!trip) return 'Chưa phân bổ'
      return this.getVehicleInfo(trip, 'driver_name')
    },

    getLicensePlate (trip) {
      if (!trip) return 'Chưa phân bổ'
      return this.getVehicleInfo(trip, 'license_plate')
    },
    getVehicleInfo (trip, propertyName) {
      if (!trip || !trip.vehicle_id) return 'Chưa phân bổ'

      // If full vehicle object is available, use it
      if (trip.vehicle && trip.vehicle[propertyName]) {
        return trip.vehicle[propertyName]
      }

      // Otherwise return a placeholder
      return 'Chưa phân bổ'
    },
    async submitRouteForm () {
      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        // Đảm bảo dữ liệu đúng định dạng
        const routeData = {
          destination_location: this.routeForm.destination_location,
          month: this.routeForm.month,
          year: this.routeForm.year,
          image_url: this.routeForm.image_url,
          price: parseFloat(this.routeForm.price) // Đảm bảo price là số
        }

        if (this.isEditing) {
          // Update existing route
          await axiosInstance.put(
            `${API_BASE_URL}/routes/${this.routeForm.route_id}`,
            routeData,
            { headers }
          )
        } else {
          // Create new route
          await axiosInstance.post(`${API_BASE_URL}/routes/`, routeData, {
            headers
          })
        }

        this.fetchRoutes()
        this.closeModal()
      } catch (error) {
        console.error('Error saving route:', error)
        if (error.response?.status === 409) {
          ToastService.error('Tuyến đường này đã tồn tại trong hệ thống.')
        } else {
          ToastService.error(
            'Có lỗi xảy ra khi lưu dữ liệu. Vui lòng thử lại sau.'
          )
        }
      }
    },
    async deleteRoute () {
      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        await axiosInstance.delete(
          `${API_BASE_URL}/admin/routes/${this.routeToDelete.route_id}`,
          { headers }
        )

        this.routes = this.routes.filter(
          route => route.route_id !== this.routeToDelete.route_id
        )
        this.showDeleteConfirm = false
        this.routeToDelete = null

        // Nếu xóa route cuối cùng trong trang và không phải trang đầu tiên, quay về trang trước
        if (this.routes.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1
          this.fetchRoutes()
        }
      } catch (error) {
        console.error('Error deleting route:', error)
        if (error.response?.status === 409) {
          ToastService.error(
            'Không thể xóa tuyến đường này vì đang có dữ liệu liên quan.'
          )
        } else {
          ToastService.error(
            'Có lỗi xảy ra khi xóa dữ liệu. Vui lòng thử lại sau.'
          )
        }
      }
    },
    toggleSidebar () {
      this.isSidebarOpen = !this.isSidebarOpen
      localStorage.setItem('sidebarOpen', this.isSidebarOpen)
    },
    viewRouteDetails (route) {
      this.selectedRoute = route
      this.showDetailsModal = true
      this.fetchRelatedTrips(route.route_id)
    },

    closeDetailsModal () {
      this.showDetailsModal = false
      this.selectedRoute = null
      this.relatedTrips = []
      this.expandedTrips = []
    },

    async fetchRelatedTrips (routeId) {
      this.isLoadingTrips = true
      this.relatedTrips = []

      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        const response = await axiosInstance.get(
          `${API_BASE_URL}/admin/routes/?route_id=${routeId}&related=trips`,
          { headers }
        )

        // Get trips
        const trips = response.data

        // Fetch vehicle details for each trip
        for (const trip of trips) {
          if (trip.vehicle_id) {
            try {
              const vehicleResponse = await axiosInstance.get(
                `${API_BASE_URL}/vehicles/?vehicle_id=${trip.vehicle_id}`,
                { headers }
              )
              if (vehicleResponse.data && vehicleResponse.data.length > 0) {
                trip.vehicle = vehicleResponse.data[0]
              }
            } catch (err) {
              console.error(
                `Error fetching vehicle for trip ${trip.trip_id}:`,
                err
              )
            }
          }
        }

        this.relatedTrips = trips
      } catch (error) {
        console.error('Lỗi khi tải dữ liệu chuyến đi:', error)
      } finally {
        this.isLoadingTrips = false
      }
    },

    toggleTripDetails (tripId) {
      if (this.expandedTrips.includes(tripId)) {
        this.expandedTrips = this.expandedTrips.filter(id => id !== tripId)
      } else {
        this.expandedTrips.push(tripId)
      }
    },

    formatDateTime (dateTimeString) {
      if (!dateTimeString) return '-'

      try {
        return new Date(dateTimeString).toLocaleString('vi-VN', {
          weekday: 'long',
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (error) {
        console.error('Date formatting error:', error)
        return dateTimeString
      }
    },

    translateTripStatus (status) {
      const statusMap = {
        scheduled: 'Đã lên lịch',
        in_progress: 'Đang chạy',
        completed: 'Đã hoàn thành',
        canceled: 'Đã hủy'
      }

      return statusMap[status] || status
    },

    formatVehicleType (type) {
      const types = {
        '16_slots': 'Xe 16 chỗ',
        '28_slots': 'Xe 28 chỗ',
        '45_slots': 'Xe 45 chỗ',
        over_night: 'Xe giường nằm'
      }

      return types[type] || type
    }
  },
  mounted () {
    const sidebarState = localStorage.getItem('sidebarOpen')
    if (sidebarState !== null) this.isSidebarOpen = sidebarState === 'true'
    this.fetchRoutes()
  }
}
</script>

<style scoped>
@import '@/assets/styles/admin.scss';
@import '@/assets/styles/admin-table.scss';

.details-modal {
  max-width: 800px;
  width: 90%;
}

.details-section {
  margin-bottom: 24px;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 16px;
}

.details-section h3 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.1rem;
  margin-bottom: 16px;
  color: #3182ce;
}

.details-section h3 i {
  color: #4299e1;
}

.route-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

.info-item {
  display: flex;
  flex-direction: column;
  padding: 12px;
  background-color: #f7fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.info-item .label {
  font-size: 0.8rem;
  color: #718096;
  margin-bottom: 4px;
}

.info-item .value {
  font-weight: 500;
  color: #2d3748;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px;
  background: #f8fafc;
  border-radius: 8px;
  color: #718096;
}

.empty-state i {
  font-size: 2rem;
  margin-bottom: 16px;
  color: #a0aec0;
}

.loading-indicator {
  text-align: center;
  padding: 24px;
  color: #718096;
}

.trips-accordion {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.trip-item {
  border-bottom: 1px solid #e2e8f0;
}

.trip-item:last-child {
  border-bottom: none;
}

.trip-header {
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8fafc;
  cursor: pointer;
  transition: background-color 0.2s;
}

.trip-header:hover {
  background: #edf2f7;
}

.trip-summary {
  display: flex;
  align-items: center;
  gap: 16px;
}

.trip-time {
  font-weight: 500;
  color: #2d3748;
  display: flex;
  align-items: center;
  gap: 8px;
}

.trip-time i {
  color: #4299e1;
}

.trip-status {
  font-size: 0.85rem;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 500;
}

.trip-status.scheduled {
  background: #e9f6fd;
  color: #2b6cb0;
}

.trip-status.in_progress {
  background: #c6f6d5;
  color: #2f855a;
}

.trip-status.completed {
  background: #c3dafe;
  color: #4c51bf;
}

.trip-status.canceled {
  background: #fed7d7;
  color: #c53030;
}

.trip-details {
  padding: 16px;
  background: white;
  border-top: 1px solid #e2e8f0;
}

.trip-detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}

.trip-detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.trip-detail-item .label {
  font-size: 0.8rem;
  color: #718096;
}

.trip-detail-item .value {
  font-weight: 500;
  color: #2d3748;
}

.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 500;
  justify-content: left;
}

.close-details-btn {
  background-color: #e2e8f0;
  color: #4a5568;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.close-details-btn:hover {
  background-color: #cbd5e0;
}

.action-btn.view {
  background: #4299e1;
  color: white;
}

.action-btn.view:hover {
  background: #3182ce;
}

@media (max-width: 640px) {
  .route-info-grid,
  .trip-detail-grid {
    grid-template-columns: 1fr;
  }

  .trip-summary {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}

.modal {
  display: flex;
  align-items: center;
  justify-content: center;
}

.details-modal {
  max-width: 800px;
  width: 90%;
  max-height: 90vh; /* Limit height to 90% of viewport height */
  display: flex;
  flex-direction: column;
}

.details-modal .modal-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  border-radius: 0.5rem 0.5rem 0 0;
}

.details-modal .modal-body {
  overflow-y: auto; /* Add scrolling to the body */
  padding: 20px;
  max-height: calc(90vh - 130px); /* Account for header and footer heights */
}

.details-modal .modal-footer {
  position: sticky;
  bottom: 0;
  z-index: 10;
  background: white;
  border-top: 1px solid #e2e8f0;
  padding: 16px 20px;
  border-radius: 0 0 0.5rem 0.5rem;
}

/* Keep your other styles */

@media (max-width: 640px) {
  .route-info-grid,
  .trip-detail-grid {
    grid-template-columns: 1fr;
  }

  .trip-summary {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .details-modal {
    width: 95%;
    max-height: 95vh;
  }

  .details-modal .modal-body {
    max-height: calc(95vh - 120px);
    padding: 16px;
  }
}

/* Add smooth scrolling */
.details-modal .modal-body {
  scroll-behavior: smooth;
}

/* Style for scrollbar */
.details-modal .modal-body::-webkit-scrollbar {
  width: 8px;
}

.details-modal .modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.details-modal .modal-body::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 4px;
}

.details-modal .modal-body::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}
</style>
